<template>
  <Card
    v-if="
      nextSessionRef &&
      (groupRolesRef?.includes(RoleEnumTypeV2.MENTEE) || groupRolesRef?.includes(RoleEnumTypeV2.MENTOR))
    "
    class="p-3 gap-3 group-data-[collapsible=icon]:hidden"
  >
    <CardContent class="p-0 flex flex-col gap-2">
      <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide">{{ t('widget.nextSession.title') }}</p>
      <div class="flex items-center gap-3">
        <div class="flex flex-col items-center text-destructive leading-none font-semibold tabular-nums">
          <span class="text-xs capitalize">{{ extractShortDay(nextSessionRef.from) }}</span>
          <span class="text-xl">{{ extractDayNumber(nextSessionRef.from) }}</span>
        </div>
        <Separator orientation="vertical" class="h-8" />
        <p class="text-sm font-semibold truncate">
          {{ nextSessionRef.name ? nextSessionRef.name : t('widget.nextSession.name') }}
        </p>
      </div>
      <div class="flex items-center justify-between text-xs text-muted-foreground">
        <span class="first-letter:capitalize">{{ relativeTimeToSessionStartRef }}</span>
        <span>{{ getDurationRange(nextSessionRef.from, nextSessionRef.to) }}</span>
      </div>
      <div class="flex items-center -space-x-2">
        <ShadcnAvatar v-if="mentorRef" class="size-7 border-2 border-background">
          <ShadcnAvatarImage v-if="mentorRef.picture" :src="PROFILE_PICTURE_BUCKET_URL + mentorRef.picture" :alt="`${mentorRef.firstName} ${mentorRef.lastName}`" />
          <ShadcnAvatarFallback class="text-xs">{{ mentorRef.firstName?.[0] }}{{ mentorRef.lastName?.[0] }}</ShadcnAvatarFallback>
        </ShadcnAvatar>
        <template v-for="mentee in menteesRef" :key="mentee.id">
          <ShadcnAvatar class="size-7 border-2 border-background">
            <ShadcnAvatarImage v-if="mentee.picture" :src="PROFILE_PICTURE_BUCKET_URL + mentee.picture" :alt="`${mentee.firstName} ${mentee.lastName}`" />
            <ShadcnAvatarFallback class="text-xs">{{ mentee.firstName?.[0] }}{{ mentee.lastName?.[0] }}</ShadcnAvatarFallback>
          </ShadcnAvatar>
        </template>
      </div>
    </CardContent>
    <CardFooter v-if="isSessionPlannedNextHoursRef" class="p-0">
      <Button class="w-full" size="sm" @click="joinSession">
        <VideoIcon class="size-4" />
        {{ t('widget.nextSession.join') }}
      </Button>
    </CardFooter>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'
import { VideoIcon } from 'lucide-vue-next'

import { Avatar as ShadcnAvatar, AvatarFallback as ShadcnAvatarFallback, AvatarImage as ShadcnAvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import { getMentees, getMentor } from '@/features/groups/utils'
import useSessions from '@/features/sessions/hooks/use-sessions.hook'
import { getPlannedSessions, sortSessionsByDate } from '@/features/sessions/utils'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useRouter from '@/hooks/use-router.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'
import { extractDayNumber, extractShortDay, getDurationRange, getRelativeTimeToDate } from '@/utils/dates'
import { PROFILE_PICTURE_BUCKET_URL } from '@/utils/config/constants'

const { t } = useI18n()
const router = useRouter()
const { sessionsRef } = useSessions()
const plannedSessionsRef = computed(() => sessionsRef.value.filter(getPlannedSessions))

const nextSessionRef = computed(() => {
  const sessions = plannedSessionsRef.value
    .filter((session) => new Date(session.to).getTime() > Date.now())
    .sort(sortSessionsByDate)

  return sessions[0] || null
})

const groupIdRef = computed(() => nextSessionRef.value?.mentoringGroupId ?? null)
const { groupRolesRef } = useGroupRoles({ groupIdRef })

const mentorRef = computed(() => nextSessionRef.value?.users.find(getMentor))
const menteesRef = computed(() => nextSessionRef.value?.users.filter(getMentees))

const relativeTimeToSessionStartRef = ref(getRelativeTimeToDate(nextSessionRef.value?.from))
const isSessionPlannedNextHoursRef = ref(false)
const isSessionInProgressRef = ref(false)

function getSessionTime() {
  const now = dayjsClient()
  const sessionStart = dayjsClient(nextSessionRef.value?.from)
  const sessionEnd = dayjsClient(nextSessionRef.value?.to)

  isSessionInProgressRef.value = now.isAfter(sessionStart) && now.isBefore(sessionEnd)
  relativeTimeToSessionStartRef.value = isSessionInProgressRef.value
    ? t('widget.nextSession.inProgress')
    : getRelativeTimeToDate(nextSessionRef.value?.from)

  const diff = sessionStart.diff(now, 'hours')
  isSessionPlannedNextHoursRef.value = diff <= 2 || isSessionInProgressRef.value
}

watchEffect(() => {
  if (!nextSessionRef.value?.from) return
  getSessionTime()
  const interval = setInterval(getSessionTime, 1000 * 60)
  return () => clearInterval(interval)
})

function joinSession() {
  if (!nextSessionRef.value) return
  router.push({ query: { ...router.currentRoute.query, meetingRoomId: nextSessionRef.value.meetingRoomId } })
}
</script>
